// Imports & Exports tool — outputs: table copy (paste into Excel / email), Excel workbook,
// and the Hasbro-branded PDF packing list for the customs broker.
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { lineCalc, totals, byHsCode, recommend } from "./rules";

const HEAD = ["#", "Material", "Description", "HS code", "Qty", "Cartons", "Units/ctn", "CBM", "Net kg", "Gross kg", "Unit value", "Value"];
const fmt = (n, dp = 2) => (n == null ? "" : Number(n).toLocaleString("en-AU", { minimumFractionDigits: dp, maximumFractionDigits: dp }));
const fileBase = d => `hasbro-${d.direction.toLowerCase()}-${(d.reference || "shipment").replace(/[^A-Za-z0-9-]+/g, "_")}-${d.date}`;

function lines(rows) {
    return rows.map((r, i) => {
        const c = lineCalc(r);
        return [i + 1, r.code || "", r.description || "", r.hs || "", r.qty, c.cartons, r.unitsPerCarton, c.cbm, c.netKg, c.grossKg, r.unitValue, c.value];
    });
}

/** Tab-separated table (header, lines, total) — pastes cleanly into Excel, Sheets or an email */
export function tableText(doc, rows) {
    const t = totals(rows);
    const out = [HEAD, ...lines(rows), ["", "TOTAL", "", "", t.qty, t.cartons, "", t.cbm, t.netKg, t.grossKg, "", t.value]];
    return [`${doc.direction} · ${doc.reference || "—"} · ${doc.date} · ${doc.origin || "—"} → ${doc.destination || "—"} · ${doc.incoterm} · ${doc.currency}`, "", ...out.map(r => r.map(v => (v == null ? "" : v)).join("\t"))].join("\n");
}

export function downloadExcel(doc, rows) {
    const t = totals(rows), rec = recommend(t);
    const wb = XLSX.utils.book_new();
    const info = [
        ["Hasbro — " + (doc.direction === "Import" ? "Import" : "Export") + " packing list"], [],
        ["Reference", doc.reference], ["Date", doc.date], ["Origin", doc.origin], ["Destination", doc.destination],
        ["Incoterm", doc.incoterm], ["Currency", doc.currency], [],
        ["Lines", t.lines], ["Total quantity", t.qty], ["Total cartons", t.cartons], ["Total CBM", t.cbm],
        ["Total net kg", t.netKg], ["Total gross kg", t.grossKg], [`Total value (${doc.currency})`, t.value], ["Pallets (est.)", t.pallets], [],
        ["Recommended", rec.mixText || "—"], ["Why", rec.reason], [],
        ["Material data", "SAMPLE values — verify HS codes, weights and values before lodging with customs."],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(info), "Summary");
    const ws = XLSX.utils.aoa_to_sheet([HEAD, ...lines(rows), ["", "TOTAL", "", "", t.qty, t.cartons, "", t.cbm, t.netKg, t.grossKg, "", t.value]]);
    ws["!cols"] = [4, 12, 38, 12, 8, 8, 9, 9, 10, 10, 10, 12].map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws, "Lines");
    const hs = byHsCode(rows);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ["HS code", "Lines", "Qty", "Cartons", "CBM", "Net kg", "Gross kg", `Value (${doc.currency})`],
        ...hs.map(g => [g.hs, g.lines, g.qty, g.cartons, g.cbm, g.netKg, g.grossKg, g.value]),
    ]), "By HS code");
    XLSX.writeFile(wb, `${fileBase(doc)}.xlsx`);
}

async function logoDataUrl() {
    try {
        const blob = await (await fetch("/hasbro-logo.png")).blob();
        return await new Promise(res => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = () => res(null); r.readAsDataURL(blob); });
    } catch { return null; }
}

/** Hasbro-branded packing list PDF (A4 landscape): header, shipment details, lines, HS summary, totals */
export async function buildPdf(doc, rows) {
    const t = totals(rows), rec = recommend(t), hs = byHsCode(rows);
    const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const W = pdf.internal.pageSize.getWidth();
    const BLUE = [0, 101, 160], NAVY = [0, 59, 107];

    pdf.setFillColor(...NAVY); pdf.rect(0, 0, W, 24, "F");
    const logo = await logoDataUrl();
    if (logo) pdf.addImage(logo, "PNG", 10, 4, 16, 16);
    pdf.setTextColor(255); pdf.setFont("helvetica", "bold"); pdf.setFontSize(15);
    pdf.text(`${doc.direction === "Import" ? "Import" : "Export"} packing list`, logo ? 30 : 10, 12);
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(9);
    pdf.text("Hasbro Logistics", logo ? 30 : 10, 18);
    pdf.text(`Reference ${doc.reference || "—"}   ·   Date ${doc.date}`, W - 10, 12, { align: "right" });
    pdf.text(`Prepared ${new Date().toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}`, W - 10, 18, { align: "right" });

    pdf.setTextColor(30);
    autoTable(pdf, {
        startY: 30, theme: "plain", styles: { fontSize: 9, cellPadding: 1.2 }, columnStyles: { 0: { fontStyle: "bold", cellWidth: 28 }, 2: { fontStyle: "bold", cellWidth: 28 } },
        body: [
            ["Direction", doc.direction, "Incoterm", doc.incoterm],
            ["Origin", doc.origin || "—", "Destination", doc.destination || "—"],
            ["Currency", doc.currency, "Recommended", rec.mixText || "—"],
        ],
        margin: { left: 10, right: 10 },
    });

    autoTable(pdf, {
        startY: pdf.lastAutoTable.finalY + 4, head: [HEAD],
        body: lines(rows).map(r => r.map((v, k) => (k >= 7 ? fmt(v, k === 7 ? 3 : 2) : k >= 4 ? (v == null ? "" : Number(v).toLocaleString("en-AU")) : v))),
        foot: [["", "TOTAL", "", "", t.qty.toLocaleString("en-AU"), t.cartons.toLocaleString("en-AU"), "", fmt(t.cbm, 3), fmt(t.netKg), fmt(t.grossKg), "", fmt(t.value)]],
        styles: { fontSize: 8, cellPadding: 1.6 }, headStyles: { fillColor: BLUE }, footStyles: { fillColor: [230, 241, 248], textColor: 20, fontStyle: "bold" },
        columnStyles: { 0: { cellWidth: 8 }, 2: { cellWidth: 62 }, 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" }, 9: { halign: "right" }, 10: { halign: "right" }, 11: { halign: "right" } },
        margin: { left: 10, right: 10 },
    });

    autoTable(pdf, {
        startY: pdf.lastAutoTable.finalY + 6,
        head: [["Totals by HS code", "Lines", "Qty", "Cartons", "CBM", "Net kg", "Gross kg", `Value (${doc.currency})`]],
        body: hs.map(g => [g.hs, g.lines, g.qty.toLocaleString("en-AU"), g.cartons.toLocaleString("en-AU"), fmt(g.cbm, 3), fmt(g.netKg), fmt(g.grossKg), fmt(g.value)]),
        styles: { fontSize: 8, cellPadding: 1.6 }, headStyles: { fillColor: NAVY },
        columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" } },
        margin: { left: 10, right: W / 2 },
    });

    const pages = pdf.getNumberOfPages();
    for (let p = 1; p <= pages; p++) {
        pdf.setPage(p);
        pdf.setFontSize(7); pdf.setTextColor(120);
        pdf.text("Material data are SAMPLE values — verify HS codes, weights and values before lodging with customs.", 10, pdf.internal.pageSize.getHeight() - 6);
        pdf.text(`Page ${p} of ${pages}`, W - 10, pdf.internal.pageSize.getHeight() - 6, { align: "right" });
    }
    return pdf;
}

export async function downloadPdf(doc, rows) {
    (await buildPdf(doc, rows)).save(`${fileBase(doc)}.pdf`);
}

/** Native share sheet with the PDF attached where the device supports it; otherwise false */
export async function sharePdf(doc, rows) {
    const pdf = await buildPdf(doc, rows);
    const file = new File([pdf.output("blob")], `${fileBase(doc)}.pdf`, { type: "application/pdf" });
    if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: `Hasbro ${doc.direction.toLowerCase()} ${doc.reference || ""}`.trim() });
        return true;
    }
    return false;
}

/** Plain-text summary for an email body */
export function emailSummary(doc, rows) {
    const t = totals(rows), rec = recommend(t);
    return [
        `${doc.direction} ${doc.reference || ""} — ${doc.date}`.trim(),
        `${doc.origin || "—"} → ${doc.destination || "—"} · ${doc.incoterm} · ${doc.currency}`, "",
        `Lines: ${t.lines} · Qty: ${t.qty.toLocaleString("en-AU")} · Cartons: ${t.cartons.toLocaleString("en-AU")}`,
        `CBM: ${fmt(t.cbm, 3)} · Net kg: ${fmt(t.netKg)} · Gross kg: ${fmt(t.grossKg)} · Value: ${doc.currency} ${fmt(t.value)}`,
        `Recommended: ${rec.mixText || "—"}`, "",
        "By HS code:", ...byHsCode(rows).map(g => `  ${g.hs}: ${g.qty.toLocaleString("en-AU")} units, ${fmt(g.cbm, 3)} cbm, ${fmt(g.grossKg)} kg, ${doc.currency} ${fmt(g.value)}`),
        "", "Full packing list attached (PDF / Excel).",
    ].join("\n");
}
